/**
 * Animated Modal Component
 * 
 * A comprehensive modal component with smooth animations and transitions.
 * Built with Framer Motion for fluid animations and includes features like
 * backdrop blur, 3D transforms, and touch support.
 * 
 * Features:
 * - Smooth entrance/exit animations
 * - Backdrop blur effect
 * - 3D perspective transforms
 * - Touch device support
 * - Outside click to close
 * - Keyboard navigation
 * - Responsive design
 * - Dark mode support
 */

'use client';
import { cn } from '@/utils/utils';
import { AnimatePresence, motion } from 'framer-motion';
import React, { ReactNode, createContext, useContext, useEffect, useRef, useState } from 'react';

/**
 * Modal context type definition
 * @interface ModalContextType
 * @property {boolean} open - Whether the modal is open
 * @property {(open: boolean) => void} setOpen - Function to set modal open state
 */
interface ModalContextType {
	open: boolean;
	setOpen: (open: boolean) => void;
}

/**
 * Modal context for sharing state
 * @constant
 */
const ModalContext = createContext<ModalContextType | undefined>(undefined);

/**
 * Modal Provider Component
 * 
 * @component
 * @param {Object} props
 * @param {ReactNode} props.children - Child components
 * @returns {JSX.Element} Modal provider
 */
export const ModalProvider = ({ children }: { children: ReactNode }) => {
	const [open, setOpen] = useState(false);

	return <ModalContext.Provider value={{ open, setOpen }}>{children}</ModalContext.Provider>;
};

/**
 * Hook to access modal context
 * @throws {Error} If used outside of ModalProvider
 * @returns {ModalContextType} Modal context
 */
export const useModal = () => {
	const context = useContext(ModalContext);
	if (!context) {
		throw new Error('useModal must be used within a ModalProvider');
	}
	return context;
};

/**
 * Modal Root Component
 * 
 * @component
 * @param {Object} props
 * @param {ReactNode} props.children - Modal content
 * @returns {JSX.Element} Modal root
 */
export function Modal({ children }: { children: ReactNode }) {
	return <ModalProvider>{children}</ModalProvider>;
}

/**
 * Modal Trigger Component
 * 
 * @component
 * @param {Object} props
 * @param {ReactNode} props.children - Trigger content
 * @param {string} [props.className] - Additional CSS classes
 * @returns {JSX.Element} Modal trigger
 */
export const ModalTrigger = ({
	children,
	className,
}: {
	children: ReactNode;
	className?: string;
}) => {
	const { setOpen } = useModal();
	return (
		<button
			className={cn(
				'px-4 py-2 rounded-md text-black dark:text-white text-center relative overflow-hidden',
				className,
			)}
			onClick={() => setOpen(true)}
		>
			{children}
		</button>
	);
};

/**
 * Modal Body Component
 * 
 * @component
 * @param {Object} props
 * @param {ReactNode} props.children - Modal content
 * @param {string} [props.className] - Additional CSS classes
 * @returns {JSX.Element} Modal body
 */
export const ModalBody = ({ children, className }: { children: ReactNode; className?: string }) => {
	const { open } = useModal();

	/**
	 * Handles body scroll locking when modal is open
	 * @effect
	 */
	useEffect(() => {
		if (open) {
			document.body.style.overflow = 'hidden';
		} else {
			document.body.style.overflow = 'auto';
		}
	}, [open]);

	const modalRef = useRef(null);
	const { setOpen } = useModal();
	useOutsideClick(modalRef, () => setOpen(false));

	/**
	 * Handles modal close event
	 * @effect
	 */
	useEffect(() => {
		const handleCloseModal = () => setOpen(false);
		window.addEventListener('close-modal', handleCloseModal);
		return () => window.removeEventListener('close-modal', handleCloseModal);
	}, [setOpen]);

	return (
		<AnimatePresence>
			{open && (
				<motion.div
					initial={{
						opacity: 0,
					}}
					animate={{
						opacity: 1,
						backdropFilter: 'blur(10px)',
					}}
					exit={{
						opacity: 0,
						backdropFilter: 'blur(0px)',
					}}
					className="fixed [perspective:800px] [transform-style:preserve-3d] inset-0 h-full w-full  flex items-center justify-center z-50"
				>
					<Overlay />

					<motion.div
						ref={modalRef}
						className={cn(
							'max-h-[90vh] w-[95%] md:max-w-[60%] bg-[#1B1B1C] border border-white/20 md:rounded-2xl relative z-50 flex flex-col overflow-hidden',
							className,
						)}
						initial={{
							opacity: 0,
							scale: 0.5,
							rotateX: 40,
							y: 40,
						}}
						animate={{
							opacity: 1,
							scale: 1,
							rotateX: 0,
							y: 0,
						}}
						exit={{
							opacity: 0,
							scale: 0.8,
							rotateX: 10,
						}}
						transition={{
							type: 'spring',
							stiffness: 260,
							damping: 15,
						}}
					>
						<CloseIcon />
						<div className="overflow-y-auto flex-1">{children}</div>
					</motion.div>
				</motion.div>
			)}
		</AnimatePresence>
	);
};

/**
 * Modal Content Component
 * 
 * @component
 * @param {Object} props
 * @param {ReactNode} props.children - Content
 * @param {string} [props.className] - Additional CSS classes
 * @returns {JSX.Element} Modal content
 */
export const ModalContent = ({
	children,
	className,
}: {
	children: ReactNode;
	className?: string;
}) => {
	return <div className={cn('flex flex-col flex-1 p-8 md:p-10', className)}>{children}</div>;
};

/**
 * Modal Footer Component
 * 
 * @component
 * @param {Object} props
 * @param {ReactNode} props.children - Footer content
 * @param {string} [props.className] - Additional CSS classes
 * @returns {JSX.Element} Modal footer
 */
export const ModalFooter = ({
	children,
	className,
}: {
	children: ReactNode;
	className?: string;
}) => {
	return (
		<div className={cn('flex justify-end p-4 bg-gray-100 dark:bg-neutral-900', className)}>
			{children}
		</div>
	);
};

/**
 * Modal Overlay Component
 * 
 * @component
 * @param {Object} props
 * @param {string} [props.className] - Additional CSS classes
 * @returns {JSX.Element} Modal overlay
 */
const Overlay = ({ className }: { className?: string }) => {
	return (
		<motion.div
			initial={{
				opacity: 0,
			}}
			animate={{
				opacity: 1,
				backdropFilter: 'blur(10px)',
			}}
			exit={{
				opacity: 0,
				backdropFilter: 'blur(0px)',
			}}
			className={`fixed inset-0 h-full w-full bg-black bg-opacity-50 z-50 ${className}`}
		></motion.div>
	);
};

/**
 * Modal Close Icon Component
 * 
 * @component
 * @returns {JSX.Element} Close icon
 */
const CloseIcon = () => {
	const { setOpen } = useModal();
	return (
		<button
			onClick={() => setOpen(false)}
			onTouchEnd={(e) => {
				e.preventDefault();
				setOpen(false);
			}}
			className="absolute top-4 right-4 group p-4 -m-4 touch-manipulation"
			aria-label="Close modal"
		>
			<svg
				xmlns="http://www.w3.org/2000/svg"
				width="24"
				height="24"
				viewBox="0 0 24 24"
				fill="none"
				stroke="currentColor"
				strokeWidth="2"
				strokeLinecap="round"
				strokeLinejoin="round"
				className="text-black dark:text-white h-6 w-6 group-hover:scale-125 group-hover:rotate-3 transition duration-200"
				aria-hidden="true"
			>
				<path stroke="none" d="M0 0h24v24H0z" fill="none" />
				<path d="M18 6l-12 12" />
				<path d="M6 6l12 12" />
			</svg>
		</button>
	);
};

/**
 * Type definition for outside click callback
 * @typedef {(event: MouseEvent | TouchEvent) => void} OutsideClickCallback
 */
type OutsideClickCallback = (event: MouseEvent | TouchEvent) => void;

/**
 * Hook to handle clicks outside of a component
 * 
 * @param {React.RefObject<HTMLDivElement>} ref - Reference to the component
 * @param {OutsideClickCallback} callback - Callback to execute on outside click
 */
export const useOutsideClick = (
	ref: React.RefObject<HTMLDivElement>,
	callback: OutsideClickCallback,
) => {
	useEffect(() => {
		const listener = (event: MouseEvent | TouchEvent) => {
			if (!ref.current || ref.current.contains(event.target as Node)) {
				return;
			}
			callback(event);
		};

		document.addEventListener('mousedown', listener);
		document.addEventListener('touchstart', listener);

		return () => {
			document.removeEventListener('mousedown', listener);
			document.removeEventListener('touchstart', listener);
		};
	}, [ref, callback]);
};
